/**
 * Converts a snapshot of the old Google Sheet into ordinary commands.
 *
 * Because the import is just commands, imported rows get the same history, checks and undo
 * as anything typed in later. Every command id comes from its place in the Sheet
 * ("import:item:r5" is the 5th receipt row), so running the import twice never duplicates
 * anything: the server recognizes the ids and skips them.
 *
 * The snapshot format is what scripts/import_sheet.py writes: formatted cell text, row by row.
 */
import type { CommandEnvelope } from "../commands.ts";
import { parseMoney, parsePercentBp } from "../money.ts";
import type { IsoDate, IsoDateTime } from "../types.ts";

export interface SheetSnapshot {
  source: string;
  capturedAt: IsoDate;
  settings: {
    owner: string;
    overdueWarnDays: number;
    overdueLateDays: number;
    /** [store, "7.75%", category] */
    stores: [string, string, string][];
    categories: string[];
    cards: string[];
  };
  /** [color, name] for each of the 16 slots; empty name = unused slot. */
  people: [string, string][];
  receipt: { header: string[]; rows: string[][] };
  /** [date time, name, note, amount] */
  payments: [string, string, string, string][];
  /** [date, store, receipt total] */
  trips: [string, string, string][];
}

export interface ImportPlan {
  commands: CommandEnvelope[];
  /** Rows that couldn't be read, with the reason, so nothing is skipped silently. */
  problems: string[];
  personIds: Record<string, string>;
}

/** "10/12/2026" or "10/12/26" -> "2026-10-12". */
export function sheetDate(text: string): IsoDate | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(text.trim());
  if (!m) return null;
  const year = m[3]!.length === 2 ? `20${m[3]}` : m[3]!;
  return `${year}-${m[1]!.padStart(2, "0")}-${m[2]!.padStart(2, "0")}`;
}

/** "10/13/26 10:43" -> "2026-10-13T10:43". A date without a time becomes noon. */
export function sheetDateTime(text: string): IsoDateTime | null {
  const [d = "", t = ""] = text.trim().split(/\s+/);
  const date = sheetDate(d);
  if (!date) return null;
  if (t === "") return `${date}T12:00`;
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return null;
  return `${date}T${m[1]!.padStart(2, "0")}:${m[2]}`;
}

function slug(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "x"
  );
}

export function planSheetImport(snap: SheetSnapshot): ImportPlan {
  const commands: CommandEnvelope[] = [];
  const problems: string[] = [];
  const push = (cmdId: string, cmd: CommandEnvelope["cmd"]) =>
    commands.push({ cmdId: `import:${cmdId}`, cmd });

  push("mark", { type: "import.mark", source: "Google Sheet", capturedAt: snap.capturedAt });

  // Store presets, then categories, cards and overdue settings
  for (const [store, pct, category] of snap.settings.stores) {
    const taxBp = parsePercentBp(pct);
    if (!store.trim() || taxBp === null) {
      problems.push(`Settings: couldn't read the preset for "${store}" (${pct})`);
      continue;
    }
    push(`store:${slug(store)}`, {
      type: "store.set",
      name: store.trim(),
      taxBp,
      category: category.trim() || "Other",
    });
  }
  push("settings", {
    type: "settings.update",
    patch: {
      categories: snap.settings.categories.filter((c) => c.trim()),
      cards: snap.settings.cards.filter((c) => c.trim()),
      overdueWarnDays: snap.settings.overdueWarnDays,
      overdueLateDays: snap.settings.overdueLateDays,
    },
  });

  // People: one per named slot, keeping the slot's color
  const personIds: Record<string, string> = {};
  for (const [color, rawName] of snap.people) {
    const name = rawName.trim();
    if (!name) continue;
    const id = `p-${slug(name)}`;
    if (Object.values(personIds).includes(id)) {
      problems.push(`People: "${name}" appears twice; names must be different`);
      continue;
    }
    personIds[name.toLowerCase()] = id;
    push(`person:${slug(name)}`, {
      type: "person.add",
      personId: id,
      name,
      color: color || "Gray",
    });
  }
  const idOf = (name: string): string | undefined => personIds[name.trim().toLowerCase()];
  const ownerId = idOf(snap.settings.owner);
  if (ownerId) push("owner", { type: "settings.update", patch: { ownerPersonId: ownerId } });

  // Receipt rows
  const h = snap.receipt.header;
  const col = (name: string) => h.indexOf(name);
  const C = {
    date: col("Date"),
    store: col("Store"),
    itemNo: col("Item #"),
    desc: col("Description"),
    category: col("Category"),
    price: col("Price"),
    coupon: col("Coupon"),
    crv: col("CRV"),
    tax: col("Tax/tip %"),
    paidWith: col("Paid with"),
    paidBy: col("Paid by"),
    custom: col("Custom split"),
    notes: col("Notes"),
    firstPerson: col("Status") + 1,
  };
  const slots = h.slice(C.firstPerson, C.firstPerson + 16);
  snap.receipt.rows.forEach((row, i) => {
    const rowNo = i + 5; // data starts on row 5 of the Receipt tab
    const cell = (c: number) => (c >= 0 ? (row[c] ?? "").trim() : "");
    const name = cell(C.desc);
    const priceText = cell(C.price);
    if (!name && !priceText) return; // blank row
    const date = sheetDate(cell(C.date));
    const price = parseMoney(priceText);
    const coupon = parseMoney(cell(C.coupon));
    const crv = parseMoney(cell(C.crv));
    const taxText = cell(C.tax);
    const taxBp = taxText === "" ? null : parsePercentBp(taxText);
    if (
      !date ||
      price === null ||
      coupon === null ||
      crv === null ||
      (taxText !== "" && taxBp === null)
    ) {
      problems.push(`Receipt row ${rowNo}: couldn't read "${name || priceText}"`);
      return;
    }
    const ticked: string[] = [];
    slots.forEach((header, j) => {
      if (cell(C.firstPerson + j).toUpperCase() !== "TRUE") return;
      const pid = idOf(header.replace(/^\(|\)$/g, ""));
      if (pid) ticked.push(pid);
      else problems.push(`Receipt row ${rowNo}: ticked for an unnamed slot (${header})`);
    });
    const paidByName = cell(C.paidBy);
    const paidBy = paidByName ? (idOf(paidByName) ?? null) : null;
    if (paidByName && !paidBy)
      problems.push(`Receipt row ${rowNo}: Paid by "${paidByName}" isn't on People`);
    push(`item:r${rowNo}`, {
      type: "item.add",
      itemId: `i-r${rowNo}`,
      fields: {
        date,
        store: cell(C.store) || "Other",
        itemNo: cell(C.itemNo),
        name: name || cell(C.store) || "(no description)",
        category: cell(C.category) || null,
        priceCents: price,
        couponCents: Math.min(0, coupon),
        crvCents: Math.max(0, crv),
        taxBp,
        paidWith: cell(C.paidWith) || null,
        paidBy,
        customSplit: cell(C.custom),
        note: cell(C.notes),
      },
      ticked,
    });
  });

  // Payments
  snap.payments.forEach(([when, who, note, amount], i) => {
    const at = sheetDateTime(when);
    const cents = parseMoney(amount);
    const pid = idOf(who);
    if (!at || cents === null || cents === 0 || !pid) {
      problems.push(`Payment ${i + 1}: couldn't read "${when} ${who} ${amount}"`);
      return;
    }
    push(`payment:${i + 1}`, {
      type: "payment.add",
      paymentId: `pay-${i + 1}`,
      at,
      personId: pid,
      cents,
      note: note.trim(),
    });
  });

  // Printed receipt totals
  for (const [d, store, total] of snap.trips) {
    const date = sheetDate(d);
    const cents = parseMoney(total);
    if (!date || cents === null || !store.trim()) {
      problems.push(`Trips: couldn't read "${d} ${store} ${total}"`);
      continue;
    }
    push(`receipt:${date}:${slug(store)}`, {
      type: "receipt.set",
      date,
      store: store.trim(),
      cents,
    });
  }

  return { commands, problems, personIds };
}
